import { Button } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ReactComponent as RightArrowIcon } from "assets/right-arrow.svg";
import DotElastic from "components/DotElastic";
import { STATUS_CODE } from "constants/constants";
import { UserDetail } from "models/User";
import { requestEmailVerification, verifyEmail } from "services/LoginService";

import styles from "./EmailVerification.module.scss";

const CODE_LENGTH = 6;

export interface VerifyEmailResponse {
  token: string;
  data: UserDetail;
}

interface EmailVerificationProps {
  email: string;
  userId: number;
  onVerified: (res?: VerifyEmailResponse) => void;
  title?: string;
  submitText?: string;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  userId,
  onVerified,
  title = "Enter verification code",
  submitText = "Continue",
}) => {
  const [sendState, setSendState] = useState<"sending" | "ready" | "failed">(
    "sending",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>(
    new Array(CODE_LENGTH).fill(""),
  );
  const [error, setError] = useState<string>("");

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const onVerifiedRef = useRef(onVerified);
  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  const sendCode = useCallback(() => {
    setSendState("sending");
    setError("");

    requestEmailVerification(email, userId)
      .then(() => {
        setVerificationCode(new Array(CODE_LENGTH).fill(""));
        setSendState("ready");
      })
      .catch((err) => {
        // Already verified elsewhere.
        if (err?.status === STATUS_CODE.CONFLICT) {
          onVerifiedRef.current();
          return;
        }
        setSendState("failed");
        setError(err?.data?.detail || "Failed to send verification code");
      });
  }, [email, userId]);

  // Send the opening code once per address, regardless of parent re-renders.
  const autoSentFor = useRef<string | null>(null);
  useEffect(() => {
    const key = `${userId}:${email}`;
    if (autoSentFor.current === key) return;
    autoSentFor.current = key;
    sendCode();
  }, [email, userId, sendCode]);

  // Auto-focus first input when loading is done
  useEffect(() => {
    if (sendState === "ready") {
      inputRefs.current[0]?.focus();
    }
  }, [sendState]);

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const { key } = e;

    // Handle backspace
    if (key === "Backspace") {
      e.preventDefault();
      const newCode = [...verificationCode];

      if (verificationCode[index]) {
        // Clear current field if it has a value
        newCode[index] = "";
      } else if (index > 0) {
        // Move to previous field and clear it
        newCode[index - 1] = "";
        inputRefs.current[index - 1]?.focus();
      }

      setVerificationCode(newCode);
      setError("");
      return;
    }

    // Handle numeric input (0-9)
    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      const newCode = [...verificationCode];
      newCode[index] = key;
      setVerificationCode(newCode);
      setError("");

      // Auto-focus next input
      if (index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    if (paste.length === CODE_LENGTH && /^[0-9]+$/.test(paste)) {
      setVerificationCode(paste.split(""));
      inputRefs.current[CODE_LENGTH - 1]?.focus();
    }
  };

  const handleSubmit = () => {
    const code = verificationCode.join("");
    if (code.length !== CODE_LENGTH) {
      setError("Please enter the complete verification code");
      return;
    }

    setIsLoading(true);
    setError("");

    verifyEmail(userId, email, code)
      .then((res) => {
        onVerified(res);
      })
      .catch((err) => {
        setError(err?.detail || "Invalid verification code. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isCodeComplete = verificationCode.every((digit) => digit !== "");

  if (sendState === "sending") {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <DotElastic />
          <p className={styles.loadingText}>Sending verification code...</p>
        </div>
      </div>
    );
  }

  if (sendState === "failed") {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <p className={styles.loadingText}>
            {error || "Failed to send verification code"}
          </p>
          <button
            type="button"
            onClick={sendCode}
            className={styles.resendLink}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.innerContainer}>
        <div className={styles.textContainer}>
          <h3>{title}</h3>
          <p>The verification code has been sent to your email {email}</p>
        </div>

        <div className={styles.codeContainer}>
          <div className={styles.codeInputs}>
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                value={digit}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`${styles.codeInput} ${digit ? styles.filled : ""}`}
                maxLength={1}
              />
            ))}
          </div>

          <div className={styles.resendContainer}>
            <button
              type="button"
              onClick={sendCode}
              className={styles.resendLink}
            >
              Didn&apos;t receive code - Resend
            </button>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}
      </div>

      <Button
        onClick={handleSubmit}
        className={`slapshotAuthButton ${!isCodeComplete ? styles.disabled : ""}`}
        disabled={isLoading || !isCodeComplete}
      >
        {isLoading ? "Verifying..." : submitText}
        <RightArrowIcon style={{ marginLeft: "12px" }} />
      </Button>
    </div>
  );
};

export default EmailVerification;
